import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Search, ArrowLeft } from "lucide-react";
import { searchForumPosts } from "@/lib/forum.functions";
import { PostCard, type ForumPostRow } from "@/components/forum/PostCard";

const TITLE = "Sök i Fritidsforum – husbil & husvagn | AK Husbil & Fritid";
const DESC =
  "Sök bland trådar, frågor och svar om husbil och husvagn. Hitta felsökning, ställplatser och tips från andra ägare.";

export const Route = createFileRoute("/forum/sok")({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q : "",
  }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Sök i Fritidsforum" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.akhusbilfritid.se/forum/sok" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:alt", content: "Sök i Fritidsforum – husbil & husvagn" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: ForumSearch,
});

function ForumSearch() {
  const { q } = Route.useSearch();
  const navigate = useNavigate({ from: "/forum/sok" });
  const search = useServerFn(searchForumPosts);
  const [term, setTerm] = useState(q);

  const { data, isLoading } = useQuery({
    queryKey: ["forum", "search", q],
    queryFn: () => search({ data: { q, limit: 30 } }),
    enabled: q.trim().length >= 2,
  });

  const rows = (data?.posts ?? []) as ForumPostRow[];

  return (
    <section className="bg-warm">
      <div className="container-edge py-12 md:py-20">
        <Link to="/forum" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={14} /> Till forumet
        </Link>

        <h1 className="mt-4 font-display text-3xl leading-tight sm:text-4xl md:text-5xl">
          Sök i <span className="font-italic-display text-accent">forumet</span>
        </h1>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            navigate({ search: { q: term.trim() } });
          }}
          className="mt-6 flex max-w-xl items-center gap-2"
        >
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="T.ex. tömma gråvatten, vinterförvaring, Rapido…"
              className="w-full rounded-full border border-input bg-background py-3 pl-11 pr-4 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </div>
          <button className="rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground">
            Sök
          </button>
        </form>

        <div className="mt-8 space-y-3">
          {q.trim().length < 2 && (
            <p className="text-sm text-muted-foreground">Skriv minst två tecken för att söka.</p>
          )}
          {isLoading && q.trim().length >= 2 && <p className="text-sm text-muted-foreground">Söker…</p>}
          {!isLoading && q.trim().length >= 2 && !rows.length && (
            <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
              <p className="font-display text-xl">Inga träffar på ”{q}”</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Prova ett annat ord — eller ställ frågan själv i forumet.
              </p>
              <Link
                to="/forum/nytt"
                className="mt-4 inline-flex rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
              >
                Ställ din fråga
              </Link>
            </div>
          )}
          {rows.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
        </div>
      </div>
    </section>
  );
}
